/**
 * Последствия сдвига работы (ПЛ-21) и заявки на закуп под работу: пересчёт на сервере с задержкой
 * и что изменится — вехи, окончание, крайние и договорные сроки, платежи и поставки, закупки, люди.
 */
import { type PurchaseRequestSummary, requestNumber as zm } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Button, IconButton } from '../../ui/button';
import { Badge, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { LField } from './LField';
import { fmtMoment } from './model';

interface Impact {
  activity: { code: string; startBefore: string; startAfter: string };
  finishBefore: string | null;
  finishAfter: string | null;
  finishShiftDays: number;
  shifted: number;
  milestones: Array<{
    id: string;
    code: string;
    name: string;
    before: string;
    after: string;
    shiftDays: number;
  }>;
  deadlines: Array<{ id: string; code: string; name: string; deadline: string; finish: string }>;
  contracts: Array<{
    id: string;
    number: string;
    subject: string;
    endDate: string;
    finish: string;
    activityCode: string;
  }>;
  contractEvents: Array<{
    contractId: string;
    number: string;
    kind: string;
    plannedDate: string;
    description: string | null;
    shiftDays: number;
    amount: number | null;
  }>;
  purchases: Array<{
    id: string;
    number: number;
    title: string;
    requiredDate: string | null;
    needBefore: string;
    needAfter: string;
    late: boolean;
  }>;
  resources: Array<{
    resourceId: string;
    name: string;
    peakBefore: number;
    peakAfter: number;
    weeksMoved: number;
  }>;
  conflictsAdded: Array<{ code: string; message: string }>;
  notes: string[];
}
interface PurchaseLink {
  id: string;
  number: number;
  title: string;
  requiredDate: string | null;
  stage: string;
}
const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const sd = (n: number) => `${n > 0 ? '+' : ''}${String(n).replace('.', ',')} раб. дн`;

export function ActivityImpact({ scheduleId, activityId }: { scheduleId: string; activityId: string }) {
  const [delay, setDelay] = useState('5');
  const m = useMutation({
    mutationFn: () =>
      api<Impact>(`/schedules/${scheduleId}/impact`, {
        body: { activityId, delayDays: Number(delay.replace(',', '.')) || 0 },
      }),
    onError: (e) => toast(e.message, 'danger'),
  });
  const r = m.data;
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[14px] font-semibold">Что будет, если сдвинуть работу</h3>
      <div className="flex flex-wrap items-end gap-2">
        <LField label="Сдвиг, рабочих дней" hint="Плюс — позже, минус — закончить раньше">
          <Input
            inputMode="decimal"
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
            className="w-32 max-md:h-11"
          />
        </LField>
        <Button loading={m.isPending} onClick={() => m.mutate()} className="max-md:h-11">
          Посчитать последствия
        </Button>
      </div>
      {r ? (
        <div className="flex flex-col gap-2 rounded-md bg-surface-2 px-3 py-2.5 text-[13px]">
          <p>
            Начало {r.activity.code}: {fmtMoment(r.activity.startBefore, true)} →{' '}
            {fmtMoment(r.activity.startAfter, true)}. Сдвинется работ: {r.shifted}.
          </p>
          <p className={clsx(r.finishShiftDays > 0 && 'font-semibold text-danger')}>
            Окончание проекта: {fmtMoment(r.finishBefore)} → {fmtMoment(r.finishAfter)}
            {r.finishShiftDays ? ` (${sd(r.finishShiftDays)})` : ' — не меняется'}
          </p>
          {r.milestones.length ? (
            <div>
              <b>Вехи</b>
              <ul className="ml-4 list-disc">
                {r.milestones.map((x) => (
                  <li key={x.id}>
                    {x.code} {x.name}: {fmtMoment(x.before)} → {fmtMoment(x.after)} ({sd(x.shiftDays)})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.deadlines.length || r.contracts.length ? (
            <div className="text-danger">
              <b>Сроки нарушаются</b>
              <ul className="ml-4 list-disc">
                {r.deadlines.map((x) => (
                  <li key={x.id}>
                    {x.code} {x.name}: крайний срок {dd(x.deadline)}, окончание {fmtMoment(x.finish)}
                  </li>
                ))}
                {r.contracts.map((x) => (
                  <li key={x.id}>
                    Договор {x.number}: срок {dd(x.endDate)}, работа {x.activityCode} закончится{' '}
                    {fmtMoment(x.finish)} — возможна неустойка
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.contractEvents.length ? (
            <div>
              <b>Платежи и поставки по договорам сдвинутых работ</b>
              <span className="block text-ink-2">
                Сдвиг — наибольший по работам договора; проверьте, к какой работе относится этап.
              </span>
              <ul className="ml-4 list-disc">
                {r.contractEvents.map((x) => (
                  <li
                    key={`${x.contractId}|${x.plannedDate}|${x.kind}|${x.description ?? ''}|${x.amount ?? ''}`}
                  >
                    {x.number}: {x.kind === 'delivery' ? 'поставка' : 'платёж'} {dd(x.plannedDate)}
                    {x.description ? ` (${x.description})` : ''} — сдвиг {sd(x.shiftDays)}
                    {x.amount != null ? `, ${tenge(x.amount)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.purchases.length ? (
            <div>
              <b>Заявки на закуп</b>
              <ul className="ml-4 list-disc">
                {r.purchases.map((x) => (
                  <li key={x.id} className={clsx(x.late && 'text-danger')}>
                    {zm(x.number)} {x.title}: нужно к {fmtMoment(x.needBefore)} → {fmtMoment(x.needAfter)}
                    {x.requiredDate ? `, в заявке ${dd(x.requiredDate)}` : ''}
                    {x.late ? ' — заявка не успевает' : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.resources.length ? (
            <div>
              <b>Люди и техника по сдвинутым работам</b>
              <ul className="ml-4 list-disc">
                {r.resources.map((x) => (
                  <li key={x.resourceId} className={clsx(x.peakAfter > x.peakBefore && 'text-danger')}>
                    {x.name}: пик {x.peakBefore} → {x.peakAfter}, меняется недель: {x.weeksMoved}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.conflictsAdded.length ? (
            <div>
              <b>Новые замечания расчёта</b>
              <ul className="ml-4 list-disc">
                {r.conflictsAdded.map((x) => (
                  <li key={`${x.code}|${x.message}`}>
                    {x.code}: {x.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {r.notes.map((n) => (
            <p key={n} className="text-ink-2">
              {n}
            </p>
          ))}
          <p className="text-ink-2">Это расчёт «что если»: график не меняется.</p>
        </div>
      ) : null}
    </section>
  );
}

export function ActivityPurchases({
  activityId,
  projectId,
  canEdit,
}: {
  activityId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const key = ['activity-purchases', activityId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<PurchaseLink[]>(`/activities/${activityId}/purchases`),
    retry: false,
  });
  const all = useQuery({
    queryKey: ['requests-for', projectId],
    queryFn: () =>
      api<PurchaseRequestSummary[]>('/procurement/requests', { query: { projectId, scope: 'all' } }),
    enabled: canEdit && q.isSuccess,
    retry: false,
  });
  const [pick, setPick] = useState('');
  const m = useMutation({
    mutationFn: (b: { requestId: string; remove?: boolean }) =>
      api<PurchaseLink[]>(`/activities/${activityId}/purchases`, { body: b }),
    onSuccess: (rows) => {
      qc.setQueryData(key, rows);
      setPick('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  // Без права на закупки блок не показывается
  if (q.isError && q.error instanceof ApiError && q.error.status === 403) return null;
  if (!q.data) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[14px] font-semibold">Закупки под работу</h3>
      {q.data.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {q.data.map((x) => (
            <li key={x.id} className="flex items-center justify-between gap-2 py-1.5">
              <span className="min-w-0">
                <Link
                  to="/procurement/$requestId"
                  params={{ requestId: x.id }}
                  className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                >
                  {zm(x.number)} {x.title}
                </Link>
                <span className="ml-2 text-[12px] text-ink-2">нужно к {dd(x.requiredDate)}</span>
              </span>
              <span className="flex items-center gap-1">
                <Badge>{x.stage}</Badge>
                {canEdit ? (
                  <IconButton
                    label="Отвязать заявку"
                    onClick={() => m.mutate({ requestId: x.id, remove: true })}
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Заявки не привязаны.</p>
      )}
      {canEdit && all.data ? (
        <div className="flex gap-2">
          <Select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="min-w-0 flex-1 max-md:h-11"
            aria-label="Привязать заявку на закуп"
          >
            <option value="">Выберите заявку…</option>
            {all.data
              .filter((r) => !q.data?.some((x) => x.id === r.id))
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.number} {r.title}
                </option>
              ))}
          </Select>
          <Button disabled={!pick} onClick={() => m.mutate({ requestId: pick })} className="max-md:h-11">
            Привязать
          </Button>
        </div>
      ) : null}
    </section>
  );
}
